import React, { useRef, useState } from 'react';

import {
  Page,
  PageContent,
  Regex,
  useFetch,
  PermissionGuard,
  useUserinfo,
  Flex,
  openNotificationWithIcon,
  Loading,
  RemoteClient,
  RemoteSession,
} from '@mss/mss-component-library';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import Sidebar from './Sidebar';
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useParams,
} from 'react-router-dom';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import RevokeSessionButton from '../../../components/RevokeSessionButton';
import Overview from './Overview';
import SessionRecording from './SessionRecording';
import useResizeObserver from 'use-resize-observer/polyfilled';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

export default function Edit() {
  const [t] = useTranslation(['remoteAccess', 'asset', 'common']);
  const [hasPermission] = useUserinfo();
  const { path, url } = useRouteMatch();
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const validId = Regex.uuid.test(sessionId);
  const [session, setSession] = useState<RemoteSession>();

  const [fetchedSession, fetchSession] = useFetch({
    url: `/api/remote-session/${sessionId}/`,
    defaultValue: {},
    onFail: () => {
      notification.error({
        message: t('Failed to get session details'),
        description: !validId
          ? t(
              'It seems there is an invalid session id in the url. Please navigate to the overview page and click on the asset there.'
            )
          : t(
              'We could not retrieve the data for this session. Please try again later.'
            ),
      });
    },
    onSucces: async ({ data }) => {
      try {
        setTimeout(() => fetchSession(), 2500);
        setSession(data);
      } catch {
        openNotificationWithIcon(
          notification.error,
          t('Failed fetch assets'),
          t(
            'Due to technical issues not all information could not be retrieved. Therefore you will only see the ids of the assets and users. Please try again later.'
          )
        );
      }
    },
  });

  return (
    <PermissionGuard redirect codename={[`admin_view_remotesession`]}>
      <Page
        breadCrumbs={
          <Breadcrumbs
            label={
              !session
                ? `${t('Loading')}...`
                : `${session.asset_object.name} (${session.user_object.name})`
            }
          />
        }
        actions={
          session &&
          session.status === 'active' && (
            <Flex>
              {hasPermission(`admin_update_remotesession`) && (
                <RevokeSessionButton
                  session={session}
                  fetchSession={fetchSession}
                />
              )}
            </Flex>
          )
        }
        main={
          fetchedSession.loading && !session ? (
            <Loading />
          ) : (
            <>
              <Switch>
                <Route
                  path={`${path}/viewer`}
                  render={() => (
                    <PageContent title={t('Session viewer')}>
                      {session!.status === 'active' ? (
                        <RemoteViewer />
                      ) : (
                        <h5>{t("This session isn't active")}</h5>
                      )}
                    </PageContent>
                  )}
                />
                <Route
                  path={`${path}/details`}
                  render={() => <Overview session={session!} />}
                />
                <Route
                  path={`${path}/recording`}
                  render={() => <SessionRecording session={session!} />}
                />
                <Redirect to={`${sanitizedUrl(url)}/details`} />
              </Switch>
            </>
          )
        }
        sidebar={<Sidebar session={session} />}
      />
    </PermissionGuard>
  );
}

const RemoteViewer = () => {
  const container = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(container.current?.clientWidth || 1);

  const onResize = (data: any) => {
    setWidth(data.width || 1);
  };

  useResizeObserver({
    ref: container,
    onResize,
  });
  return (
    <div ref={container}>
      {!!container.current && (
        <RemoteClient
          containerWidth={width}
          component="site"
          readonly
          port={'8002'}
        />
      )}
    </div>
  );
};
